import {Component, ContentChildren, OnInit, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {EditPermissions, NodeBuilder} from '../../../../../../../shared/models';
import { UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import {MenuFormNode} from './menuForm';
import { moveItemInArray, CdkDropList, CdkDrag, CdkDragPlaceholder } from '@angular/cdk/drag-drop';
import find from 'lodash/find';
import remove from 'lodash/remove';
import {swalMini} from '../../../../../../../shared/utils/utils';
import {SelectionPageComponent} from './pages/selection-page/selection-page.component';
import {PickerPageComponent} from './pages/picker-page/picker-page.component';
import {DatepickerPageComponent} from './pages/datepicker-page/datepicker-page.component';
import {InputPageComponent} from './pages/input-page/input-page.component';
import { ClickatellExpansionPanelComponent } from '../../../../../../../shared/components/clickatell-expansion-panel/clickatell-expansion-panel.component';
import { MatExpansionPanelTitle } from '@angular/material/expansion';
import { SelectMediaComponent } from '../../../../select-media/select-media.component';
import { InputValidatorsComponent } from '../../templates/input-validators/input-validators.component';
import { ActiveFormDirective } from '../../../../../../../shared/directives/active-form/active-form.directive';
import { CMSComponent } from '../../../../../../../shared/components/cms/cms.component';
import { MatCheckbox } from '@angular/material/checkbox';
import { NextSelectorComponent } from '../../../../json-to-from/next-selector/next-selector.component';
import { MatButton } from '@angular/material/button';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { MatTooltip } from '@angular/material/tooltip';
import { LoadingIconComponent } from '../../../../../../../shared/components/loading-icon/loading-icon.component';


const NODE_TYPE = 'menuForm';

@Component({
    selector: 'menu-form',
    templateUrl: './menu-form.component.html',
    styleUrls: ['./menu-form.component.scss'],
    imports: [ClickatellExpansionPanelComponent, MatExpansionPanelTitle, ReactiveFormsModule, SelectMediaComponent, InputValidatorsComponent, ActiveFormDirective, CMSComponent, MatCheckbox, NextSelectorComponent, MatButton, MatMenuTrigger, MatMenu, MatMenuItem, CdkDropList, CdkDrag, CdkDragPlaceholder, NgClass, MatTooltip, NgTemplateOutlet, LoadingIconComponent, SelectionPageComponent, PickerPageComponent, DatepickerPageComponent, InputPageComponent]
})
export class MenuFormComponent extends NodeBuilder implements OnInit {

    @ViewChild('selectionPage') selectionPage: SelectionPageComponent;
    @ViewChild('pickerPage') pickerPage: PickerPageComponent;
    @ViewChild('datePickerPage') datePickerPage: DatepickerPageComponent;
    @ViewChild('inputPage') inputPage: InputPageComponent;

    constructor(public dialog: MatDialog) {
        super(NODE_TYPE);
    }

    menuParams: UntypedFormGroup = undefined;
    configurePanelOpen = false;
    canEdit: EditPermissions;
    pages: any = [];
    selectedPage: any;
    pagesInfo: any = {
        selectionPages: {
            class: 'multi-page',
            title: 'Single/Multi Selection'
        },
        pickerPages: {
            class: 'picker-page',
            title: 'Picker'
        },
        datePickerPages: {
            class: 'date-page',
            title: 'Date Picker'
        },
        inputPage: {
            class: 'input-page',
            title: 'Input'
        }
    };
    removePageConfirmPanelOpened = '';
    loadPageInProgress = false;
    stepUniqueID: string = '';
    ngOnInit(): void {

    }

    get getStepData(): any {
        const menuParams = this.menuParams.getRawValue();

        const menuFormNode = new MenuFormNode(this.nodeType);

        menuFormNode.addMenuParams(menuParams);
        menuFormNode.addPages(this.pages);

        const nodeData = menuFormNode.nodeData;
        nodeData['next'] = menuParams.next;
        if(this.stepUniqueID !== '') {
            nodeData['metadata'].stepUniqueID = this.stepUniqueID;
        }
        return nodeData;
    }

    get isNodeValid(): boolean {
        this.menuParams.markAllAsTouched();
        this.handleErrorOnConfigure();
        return this.menuParams.valid;
    }

    set buildForm(stepData: any) {
        this.stepUniqueID = stepData.metadata?.stepUniqueID ? stepData.metadata?.stepUniqueID : '';
        const formData = MenuFormNode.toForm(stepData);
        if (!stepData) {
            return;
        }
        this.setPages(stepData);
        this.buildMenuParamsForm(formData);
    }

    setPages(stepData) {
        const { menu } = stepData;
        if (menu && menu.menuParams && menu.menuParams.pages && menu.menuParams.pages.length > 0) {
            this.pages = menu.menuParams.pages;
        }
    }

    buildMenuParamsForm(formData) {
        this.menuParams = MenuFormNode.getMenuTemplate(this.canEdit);
        this.menuParams.patchValue(formData.menuParams);
    }

    setImage(event, form) {
        form.get('imageRef').setValue(event);
    }

    saveFormConfiguration() {
        this.markPagesAsTouched();
        this.handleErrorOnConfigure();
        setTimeout(() => {
            if (this.checkInvalidPages()) {
                swalMini({
                    title: 'Validation failed, please check for errors!',
                    type: 'error',
                });
                return;
            } else {
                this.clearSelectedPage();
                this.closeConfigureFormPanel();
            }
        }, 0);
    }

    markPagesAsTouched(): void {
        this.selectionPage?.selectPageForm.markAllAsTouched();
        this.selectionPage?.selectPageFormOptions.markAllAsTouched();

        this.pickerPage?.pickerPageForm.markAllAsTouched();
        this.pickerPage?.pickerPageFormOptions.markAllAsTouched();

        this.inputPage?.inputPageForm.markAllAsTouched();
        this.datePickerPage?.datePickerPageForm.markAllAsTouched();
    }

    checkInvalidPages() {
        return find(this.pages, { valid: false });
    }

    openConfigureFormPanel() {
        this.configurePanelOpen = true;
        this.menuParams.controls['configureForm'].markAsDirty();
    }

    closeConfigureFormPanel() {
        this.handleErrorOnConfigure();
        this.configurePanelOpen = false;
    }

    handleErrorOnConfigure() {
        const configureForm = this.menuParams.controls['configureForm'];

        if (this.checkInvalidPages() || this.pages.length === 0) {
            configureForm.setErrors({'Required': 'A configured form is required'});
        } else {
            configureForm.setErrors(null);
        }
    }

    addSelectionPage() {
        this.pages.push({
            pageType: 'selectionPages',
            order: 0,
            pageHeader: '',
            pageSubHeader: '',
            multipleSelection: false,
            options: []
        });
        this.selectAddedPage();
    }

    addPickerPage() {
        this.pages.push({
            pageType: 'pickerPages',
            order: 0,
            pageHeader: '',
            pageSubHeader: '',
            pickerText: '',
            selectedItemIndex: 0,
            options: []
        });
        this.selectAddedPage();
    }

    addDatePickerPage() {
        this.pages.push({
            pageType: 'datePickerPages',
            order: 0,
            pageHeader: '',
            pageSubHeader: '',
            hintText: '',
            labelText: '',
            dateFormat: 'dd/MM/yyyy',
            defaultDate: '',
            endDate: '',
            startDate: ''
        });
        this.selectAddedPage();
    }

    addInputPage() {
        this.pages.push({
            pageType: 'inputPage',
            order: 0,
            pageHeader: '',
            pageSubHeader: '',
            regex: '',
            placeHolder: '',
            inputType: 'singleline', // 'multiline | singleline'
            labelText: '',
            prefixText: '',
            maximumCharacterCount: 0,
            keyboardType: 'default',
            required: false
        });
        this.selectAddedPage();
    }

    selectAddedPage() {
        const index = this.pages.length - 1;
        this.selectPage(index);
    }

    selectPage(index, event?) {
        if (event) {
            event.stopPropagation();
        }
        this.loadPageInProgress = true;
        setTimeout(() => {
            this.selectedPage = {
                index,
                type: this.pages[index].pageType,
                page: this.pages[index],
                newPage: !event
            };
            this.loadPageInProgress = false;
        }, 0);
    }

    clearSelectedPage() {
        this.selectedPage = null;
    }

    dropPage(event) {
        if (this.selectedPage) {
            this.clearSelectedPage();
        }

        setTimeout(() => {
            moveItemInArray(this.pages, event.previousIndex, event.currentIndex);
            this.selectPage(event.currentIndex);
        }, 0);
    }

    openRemovePageConfirmPanel(event, index) {
        event.preventDefault();
        event.stopPropagation();
        this.removePageConfirmPanelOpened = index;
    }

    closeRemovePageConfirmPanel() {
        this.removePageConfirmPanelOpened = '';
    }

    removePage(event, index) {
        event.preventDefault();
        if (this.selectedPage && this.selectedPage.index === index) {
            this.clearSelectedPage();
        }
        setTimeout(() => {
            remove(this.pages, (page, i) => {
                return i === index;
            });
            this.closeRemovePageConfirmPanel();
        }, 0);
    }

    setPageData(data) {
        this.pages[data.index] = data.page;
    }
}

